import { FC, useState } from "react";
import useEvent from "react-use-event-hook";

import {
  MAPLIBRE_PICKED_ADDRESS_PIN_COLOR,
  MAPLIBRE_PICKED_ADDRESS_PIN_GLYPH,
  MapLibreAutoPanPopup,
  MapLibreStationPinMarker,
} from "@ryde/controls";
import { ILocationInfoValid, MarkerIconType } from "@ryde/shared";

interface IPassengersMapSearchPinProps {
  location: ILocationInfoValid;
}

/**
 * The address picked in the add-station panel, before it is saved as a station.
 *
 * Clicking it names the address, as the Leaflet pin's popup did. Its own component because the auto-pan
 * hook only resolves inside the map subtree, and because the popup is per-pin state that nothing else on
 * this map needs to know about.
 */
export const PassengersMapSearchPin: FC<IPassengersMapSearchPinProps> = ({
  location,
}) => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  // Without this the click also reaches the map, whose add-station handler would treat it as a new point.
  const handleClick = useEvent((event: { originalEvent: MouseEvent }): void => {
    event.originalEvent.stopPropagation();
    setIsPopupOpen(true);
  });

  const handleClose = useEvent((): void => setIsPopupOpen(false));

  return (
    <>
      <MapLibreStationPinMarker
        latitude={location.latitude}
        longitude={location.longitude}
        type={MarkerIconType.Point}
        color={MAPLIBRE_PICKED_ADDRESS_PIN_COLOR}
        label={MAPLIBRE_PICKED_ADDRESS_PIN_GLYPH}
        onClick={handleClick}
        // Suppressed while the popup is open, or the browser's own tooltip covers it.
        title={isPopupOpen ? undefined : location.fullAddress}
        actionLabel={location.fullAddress}
      />

      <MapLibreAutoPanPopup
        isOpen={isPopupOpen}
        latitude={location.latitude}
        longitude={location.longitude}
        onClose={handleClose}
      >
        {location.fullAddress}
      </MapLibreAutoPanPopup>
    </>
  );
};
